<template>
  <div class="w-chart">
    <div class="w-toolbar">
      <div class="w-chips" v-if="latest">
        <span class="chip"><i class="fas fa-arrows-alt-v"></i><b>{{ fmt(latest.height, 1) }} m</b> average height</span>
        <span class="chip"><i class="fas fa-circle-notch"></i><b>{{ fmt(latest.diameter, 1) }} cm</b> average diameter</span>
        <span class="chip">{{ fmt(totalScans) }} measured scans</span>
      </div>
    </div>
    <div class="w-plot">
      <apexchart :key="chartKey" v-if="rows.length > 1" type="line" height="100%" :options="options" :series="series"/>
      <!-- one year of measurements: no line to draw, the figures themselves -->
      <div v-else-if="rows.length === 1" class="fg-single">
        <div class="fg-big"><i class="fas fa-arrows-alt-v"></i><b>{{ fmt(rows[0].height, 1) }}</b><span>m average height</span></div>
        <div class="fg-big"><i class="fas fa-circle-notch"></i><b>{{ fmt(rows[0].diameter, 1) }}</b><span>cm average diameter</span></div>
        <div class="fg-note">Measured in {{ rows[0].year }} · the growth curve appears with the next measurements</div>
      </div>
      <div v-else class="w-empty"><i class="fas fa-ruler-vertical"></i>{{ stats.insights ? 'No height or diameter measured yet' : 'Loading…' }}</div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { apexBase, fmt, palette, themeState } from '../theme';

export default {
  name: 'ForestGrowth',
  components: { apexchart: VueApexCharts },
  props: { stats: { type: Object, required: true } },
  computed: {
    // a theme or data change re-creates the chart: ApexCharts does not always redraw mixed charts on update
    chartKey() { return `${themeState.tick}:${JSON.stringify(this.series)}`; },
    rows() { return (this.stats.insights && this.stats.insights.growth) || []; },
    latest() {
      // the year with most measurements is the most representative one
      return this.rows.length ? [...this.rows].sort((a, b) => b.scans - a.scans)[0] : null;
    },
    totalScans() { return this.rows.reduce((a, r) => a + r.scans, 0); },
    series() {
      return [
        { name: 'Height (m)', data: this.rows.map((r) => ({ x: String(r.year), y: r.height })) },
        { name: 'Diameter (cm)', data: this.rows.map((r) => ({ x: String(r.year), y: r.diameter })) },
      ];
    },
    options() {
      void themeState.tick;
      const p = palette();
      return apexBase({
        chart: { type: 'line' },
        colors: [p.green, p.violet],
        stroke: { width: 3 },
        markers: { size: 5, strokeWidth: 2, strokeColors: p.card, hover: { sizeOffset: 3 } },
        yaxis: [
          { title: { text: 'metres' }, labels: { formatter: (v) => fmt(v, 0) } },
          { opposite: true, title: { text: 'centimetres' }, labels: { formatter: (v) => fmt(v, 0) } },
        ],
        legend: { position: 'top', horizontalAlign: 'right' },
        tooltip: {
          shared: true, intersect: false,
          y: { formatter: (v, { seriesIndex, dataPointIndex }) => `${fmt(v, 2)} ${seriesIndex === 0 ? 'm' : 'cm'}${seriesIndex === 1 ? ` · ${fmt(this.rows[dataPointIndex].scans)} scans` : ''}` },
        },
      });
    }
  },
  methods: { fmt }
};
</script>

<style scoped lang="scss">
@import '../widgets.scss';

.fg-single {
  position: absolute;
  inset: 0;
  display: flex;
  flex-wrap: wrap;
  align-content: center;
  justify-content: center;
  gap: 18px 36px;
  padding: 10px;
}

.fg-big {
  display: flex;
  align-items: baseline;
  gap: 8px;
  animation: w-row-in 0.6s cubic-bezier(.2, .8, .2, 1) both;

  i { color: var(--accent-green); align-self: center; }
  b { font-family: 'Sora', sans-serif; font-size: 2.4rem; font-weight: 800; color: var(--text-heading); line-height: 1; }
  span { color: var(--text-muted); font-size: 0.85rem; }
}

.fg-note { width: 100%; text-align: center; font-size: 0.75rem; color: var(--text-muted); }
</style>
